import { Address } from '@1inch/limit-order-sdk';
import { BitMask, BN, BytesBuilder, BytesIter } from '@1inch/byte-utils';
import assert from 'assert';
import { isHexBytes } from '../../validations';
import { add0x } from '../../utils';
import { UINT_16_MAX } from '../../constants';
import { now } from '../../utils/time';
export class SettlementPostInteractionData {
    constructor(data) {
        this.whitelist = data.whitelist;
        this.integratorFee = data?.integratorFee;
        this.bankFee = data.bankFee || 0n;
        this.resolvingStartTime = data.resolvingStartTime;
        this.customReceiver = data.customReceiver;
        if (this.integratorFee?.ratio) {
            assert(!this.integratorFee.receiver.isZero(), 'Fee receiver can not be zero when fee set');
        }
    }
    static new(data) {
        assert(data.whitelist.length, 'Whitelist can not be empty');
        let sumDelay = 0n;
        const whitelist = data.whitelist
            .map((d) => ({
            addressHalf: d.address.toString().slice(-20),
            allowFrom: d.allowFrom < data.resolvingStartTime
                ? data.resolvingStartTime
                : d.allowFrom
        }))
            .sort((a, b) => Number(a.allowFrom - b.allowFrom))
            .map((val) => {
            const delay = val.allowFrom - data.resolvingStartTime - sumDelay;
            sumDelay += delay;
            assert(delay < UINT_16_MAX, 'Too big diff between timestamps');
            return {
                delay,
                addressHalf: val.addressHalf
            };
        });
        return new SettlementPostInteractionData({
            ...data,
            whitelist
        });
    }
    static decode(data) {
        assert(isHexBytes(data), 'Post interaction data must be valid bytes string');
        const iter = BytesIter.BigInt(data);
        const flags = new BN(iter.nextByte(BytesIter.SIDE.Back));
        let bankFee = 0n;
        let integratorFee;
        let customReceiver;
        if (flags.getBit(0n)) {
            bankFee = iter.nextUint32();
        }
        if (flags.getBit(1n)) {
            const integratorFeeRatio = iter.nextUint16();
            const integratorAddress = iter.nextUint160();
            integratorFee = {
                ratio: integratorFeeRatio,
                receiver: Address.fromBigInt(integratorAddress)
            };
            if (flags.getBit(2n)) {
                customReceiver = Address.fromBigInt(iter.nextUint160());
            }
        }
        const resolvingStartTime = iter.nextUint32();
        const whitelist = [];
        while (!iter.isEmpty()) {
            const addressHalf = iter
                .nextBytes(10)
                .toString(16)
                .padStart(20, '0');
            const delay = iter.nextUint16();
            whitelist.push({
                addressHalf,
                delay
            });
        }
        return new SettlementPostInteractionData({
            integratorFee,
            bankFee,
            resolvingStartTime,
            whitelist,
            customReceiver
        });
    }
    static fromExtension(extension) {
        return SettlementPostInteractionData.decode(add0x(extension.postInteraction.slice(42)));
    }
    encode() {
        let bitMask = new BN(0n);
        const bytes = new BytesBuilder();
        if (this.bankFee) {
            bitMask = bitMask.setBit(0n, 1);
            bytes.addUint32(this.bankFee);
        }
        if (this.integratorFee?.ratio) {
            bitMask = bitMask.setBit(1n, 1);
            bytes
                .addUint16(this.integratorFee.ratio)
                .addAddress(this.integratorFee.receiver.toString());
            if (this.customReceiver && !this.customReceiver.isZero()) {
                bitMask = bitMask.setBit(2n, 1);
                bytes.addAddress(this.customReceiver.toString());
            }
        }
        bytes.addUint32(this.resolvingStartTime);
        for (const wl of this.whitelist) {
            bytes.addBytes(add0x(wl.addressHalf)).addUint16(wl.delay);
        }
        bitMask = bitMask.setMask(new BitMask(3n, 8n), BigInt(this.whitelist.length));
        bytes.addUint8(bitMask.value);
        return bytes.asHex();
    }
    canExecuteAt(executor, executionTime) {
        const addressHalf = executor.toString().slice(-20);
        let allowedFrom = this.resolvingStartTime;
        for (const whitelist of this.whitelist) {
            allowedFrom += whitelist.delay;
            if (addressHalf === whitelist.addressHalf) {
                return executionTime >= allowedFrom;
            }
            else if (executionTime < allowedFrom) {
                return false;
            }
        }
        return false;
    }
    isExclusivityPeriod(time = now()) {
        if (this.whitelist.length === 1) {
            return true;
        }
        if (this.whitelist[0].delay === this.whitelist[1].delay) {
            return false;
        }
        return time <= this.resolvingStartTime + this.whitelist[1].delay;
    }
    isExclusiveResolver(wallet) {
        const addressHalf = wallet.toString().slice(-20);
        if (this.whitelist.length === 1) {
            return addressHalf === this.whitelist[0].addressHalf;
        }
        if (this.whitelist[0].delay === this.whitelist[1].delay) {
            return false;
        }
        return addressHalf === this.whitelist[0].addressHalf;
    }
}
//# sourceMappingURL=settlement-post-interaction-data.js.map